/* global AthomBackupAPI */
import {useEffect, useRef, useState} from "react";

/**
 * Hook to manage fetching and deleting backups for Homey devices.
 * @returns {({}|boolean|(function(*): Promise<void>)|*)[]}
 */
export function useBackups() {
  const ref = useRef(null);
  const hyWebsiteRef = useRef(null);
  const cloudApiRef = useRef(null);
  const tokenRef = useRef(null);
  const apiRef = useRef(null);
  const [homeysWithBackups, setHomeysWithBackups] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch and rebuild backups/homeys state
  async function getBackups() {
    setIsLoading(true);
    try {
      // Ensure dependencies are initialized
      if (!hyWebsiteRef.current || !cloudApiRef.current) {
        throw new Error('Website not ready');
      }
      if (!apiRef.current) {
        apiRef.current = new AthomBackupAPI();
      }
      if (!tokenRef.current) {
        tokenRef.current = await cloudApiRef.current.createDelegationToken({ audience: 'backup' });
      }

      const fetchedBackups = await apiRef.current.getMyBackups({ token: tokenRef.current });

      const homeys = {};
      (fetchedBackups || []).forEach((backup) => {
        const { homeyId, homeyName } = backup;
        if (!homeys[homeyId]) {
          homeys[homeyId] = { name: homeyName, backups: [] };
        }
        homeys[homeyId].backups.push(backup);
      });

      setHomeysWithBackups(homeys);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Could not retrieve backups:', error);
      setHomeysWithBackups({});
    } finally {
      setIsLoading(false);
    }
  }

  // Remove a backup and refresh the list
  async function removeBackup(backup) {
    try {
      if (!apiRef.current) {
        apiRef.current = new AthomBackupAPI();
      }
      if (!tokenRef.current) {
        if (!cloudApiRef.current) throw new Error('Website not ready');
        tokenRef.current = await cloudApiRef.current.createDelegationToken({ audience: 'backup' });
      }
      setIsLoading(true);
      await apiRef.current.deleteMyBackup({
        token: tokenRef.current,
        backupId: backup.backupId,
        homeyId: backup.homeyId,
      });
      await getBackups();
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert(error?.message || error?.toString?.() || 'Failed to delete backup');
      setIsLoading(false);
    }
  }

  useEffect(() => {
    // Initialize once when hyWebsite is available, then use refs in functions
    const hyWebsite = window.hyWebsite;
    if (!hyWebsite) {
      // If hyWebsite isn't ready yet, wait for it
      function onReady() {
        window.removeEventListener("hyWebsiteReady", onReady);
        initAndStart();
      }
      window.addEventListener("hyWebsiteReady", onReady);
      return () => window.removeEventListener("hyWebsiteReady", onReady);
    }

    async function initAndStart() {
      if (ref.current) return;
      ref.current = true;

      hyWebsiteRef.current = window.hyWebsite;
      cloudApiRef.current = hyWebsiteRef.current.getCloudAPI();
      apiRef.current = new AthomBackupAPI();
      try {
        tokenRef.current = await cloudApiRef.current.createDelegationToken({ audience: 'backup' });
      } catch (error) {
        console.error('Failed to create delegation token for backups:', error);
      }

      await getBackups();

      // Subscribe to user changes to refresh backups
      cloudApiRef.current && cloudApiRef.current.on && cloudApiRef.current.on("user", getBackups);
    }

    initAndStart();

    return () => {
      cloudApiRef.current && cloudApiRef.current.off && cloudApiRef.current.off("user", getBackups);
    };
  }, []);

  return [homeysWithBackups, isLoading, removeBackup];
}